import axios from "axios";
import { useEffect, useRef, useState } from "react";
import SupportImage from "../assets/support.png";
import "../styles/Support.css";
import { fileToBase64 } from "../utils/fileToBase64";
import { formatSupportDate } from "../utils/formatSupportDate";
import { generateUrl } from "../utils/generateUrl";
import { verifyUser } from "../utils/user";

export type MessageType = {
    isSender: boolean;
    adminUsername?: string;
    message: any;
    date?: string;
    type: string;
};

const Support = () => {
    const fileInputRef = useRef<any>();
    const [user, setUser] = useState<any>(null);
    const messagesContainerRef = useRef<any>();
    const [room, setRoom] = useState<any>(null);
    const [messages, setMessages] = useState<any>([]);
    const [currentText, setCurrentText] = useState("");
    const [file, setFile] = useState<any>(null);
    const wsRef = useRef<any>(null);
    const chatBroadcastWsRef = useRef<any>(null);
    const [chatOpened, setChatOpened] = useState(false);
    const chatOpenedRef = useRef(false);
    const [wsConnected, setWsConnected] = useState(false);
    const [currentWidth, setCurrentWidth] = useState(window.innerWidth);
    const [currentHeight, setCurrentHeight] = useState(window.innerHeight);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [currentImage, setCurrentImage] = useState("");

    const getTotalChildrenHeight = () => {
        if (!messagesContainerRef.current) return 0;

        const children = messagesContainerRef.current.childNodes;
        let totalHeight = 0;

        children.forEach((child: any) => {
            if (child.nodeType === 1) {
                totalHeight += child.offsetHeight + 15;
            }
        });

        return totalHeight;
    };

    const scrollToBottom = () => {
        if (!messagesContainerRef.current) return;

        messagesContainerRef.current.scrollTo({
            top:
                messagesContainerRef.current?.scrollHeight ||
                getTotalChildrenHeight(),
        });
    };

    const setUpRoomAndMessages = async () => {
        if (!user) return;

        const roomId = user?.chat_room_id;

        const room = await axios
            .get(generateUrl(`chat/room/get/${roomId}/`))
            .then((res) => res.data)
            .catch((err) => err.response.data);

        const messages = await axios
            .get(generateUrl(`chat/message/list/by_room/${roomId}/`))
            .then((res) => res.data)
            .catch(() => []);

        setRoom(room);
        setUnreadMessages(room?.customer_unread_messages || 0);
        setMessages(messages?.reverse());
    };

    useEffect(() => {
        setTimeout(() => {
            scrollToBottom();
        }, 100);
    }, [messages, messagesContainerRef]);

    const connectWs = () => {
        if (
            !user ||
            wsRef.current?.readyState === WebSocket.OPEN ||
            wsRef.current?.readyState === WebSocket.CONNECTING ||
            !chatOpened
        )
            return;

        wsRef.current = new WebSocket(
            generateUrl(`chat/ws/customer/chat_room/${user?.id}`)
        );

        wsRef.current.onopen = () => {
            setWsConnected(true);
        };

        wsRef.current.onmessage = (e: any) => {
            try {
                const message = JSON.parse(e.data);

                if (message) addMessage(message);
            } catch {}
        };

        wsRef.current.onclose = async () => {
            setWsConnected(false);
            if (!chatOpenedRef.current) return;

            const profile = await verifyUser();

            if (profile) connectWs();
        };
    };

    useEffect(() => {
        const onResize = () => {
            setCurrentWidth(window.innerWidth);
            setCurrentHeight(window.innerHeight);
        };

        window.addEventListener("resize", onResize);

        const getUser = async () => {
            const newUser = await verifyUser();

            setUser(newUser || null);
        };

        getUser();

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    const connectChatBroadcast = () => {
        if (!user) return;

        chatBroadcastWsRef.current = new WebSocket(
            generateUrl(`chat/ws/customer/broadcast_room/${user.id}`)
        );

        chatBroadcastWsRef.current.onmessage = (e: any) => {
            if (chatOpenedRef.current) return;

            const audio = new Audio("/assets/notification.mp3");
            audio.play();

            let data: any;

            try {
                data = JSON.parse(e.data);
            } catch {
                data = null;
            }

            if (!data) return;

            const targetMessage = {
                content_type: data?.content_type || "text",
                content: data?.message || data.file?.content,
                author_type: data?.author_type || "worker",
                created_at: data.created_at,
            };

            addMessage(targetMessage);
            setUnreadMessages(data?.customer_unread_messages);
        };

        chatBroadcastWsRef.current.onclose = () => {
            if (!user) return;
            connectChatBroadcast();
        };
    };

    useEffect(() => {
        setUpRoomAndMessages();
        connectChatBroadcast();
    }, [user]);

    useEffect(() => {
        connectWs();

        if (user && chatOpened)
            axios
                .post(
                    generateUrl(
                        `chat/room/read_messages_for_customer/${user.id}/`
                    )
                )
                .then(() => setUnreadMessages(0));

        if (
            wsRef.current &&
            wsRef.current?.readyState !== WebSocket.CLOSED &&
            wsRef.current?.readyState !== WebSocket.CLOSING &&
            (!chatOpened || !user)
        )
            try {
                wsRef.current?.close();
                wsRef.current = null;
            } catch {}
    }, [user, chatOpened]);

    const addMessage = (message: any) => {
        setMessages((prev: any) => [...prev, message]);
    };

    const sendMessage = async () => {
        if (wsRef.current.readyState !== WebSocket.OPEN) return;

        const sendMessages = [];

        if (file) {
            const base64String = await fileToBase64(file);

            sendMessages.push({
                file: {
                    content: base64String,
                    name: file.name,
                },
            });
            setFile(null);
            fileInputRef.current.value = "";
        }

        if (currentText.trim()) {
            sendMessages.push({
                message: currentText,
            });

            setCurrentText("");
        }

        if (!sendMessages || sendMessages?.length < 1) return;

        sendMessages.forEach((message) => {
            wsRef.current.send(JSON.stringify(message));

            const targetMessage = {
                content_type: message?.message ? "text" : "image",
                content: message?.message || message.file?.content,
                author_type: "customer",
                created_at: new Date(),
            };

            addMessage(targetMessage);
        });
    };

    useEffect(() => {
        chatOpenedRef.current = chatOpened;

        const setScrolling = () => {
            document.documentElement.style.setProperty("overflow-y", "auto");
            document.documentElement.style.setProperty("overflow-x", "hidden");
        };

        if (
            currentHeight <= 720 ||
            (currentWidth <= 1060 && currentHeight <= 820) ||
            currentWidth <= 799
        ) {
            if (chatOpened) {
                document.documentElement.style.setProperty(
                    "overflow",
                    "hidden"
                );
            } else {
                setScrolling();
            }
        } else {
            setScrolling();
        }
    }, [chatOpened]);

    return user ? (
        <div style={{ display: "block" }}>
            <div
                className={`liveChat_overBlockOver__vVBBw${
                    chatOpened ? " liveChat_active__PJwGv" : ""
                }`}
            >
                <input
                    style={{ display: "none" }}
                    type="file"
                    ref={fileInputRef}
                    accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
                    onChange={(e: any) => {
                        const allowedExtensions = [
                            "png",
                            "jpeg",
                            "jpg",
                            "gif",
                            "webp",
                        ];

                        const targetFile = e.target.files[0];

                        if (
                            targetFile &&
                            (allowedExtensions.every(
                                (extension: string) =>
                                    extension !==
                                        targetFile?.type?.split("/")?.[1] || ""
                            ) ||
                                targetFile?.type?.split("/")[0] !== "image")
                        ) {
                            return;
                        }

                        setFile(targetFile || "");
                    }}
                />

                <div className="liveChat_blockOver__Nc0sc">
                    <div className="liveChat_block__DGKPr">
                        <div
                            className="liveChat_top__p_AEd"
                            style={{ position: "relative", zIndex: "1012" }}
                        >
                            <div className="liveChat_topIn__V38Uc">
                                <div className="liveChat_topLeft__msq_B">
                                    <div>
                                        <div className="liveChat_topTitle__VMORQ">
                                            We typically reply in a few minutes
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="liveChat_cross__wGabt"
                                    onClick={() => setChatOpened(false)}
                                >
                                    <svg
                                        width={20}
                                        height={20}
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M18 2L2 18"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M2 2L18 18"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div
                            className="liveChat_messages__VMGEY"
                            ref={messagesContainerRef}
                        >
                            <div className="liveChat_message__NWRRw">
                                <img
                                    src={SupportImage}
                                    className="liveChat_avatar__QU_yO liveChat_def__4MOxM"
                                />

                                <div className="liveChat_messageOver__DpX_v">
                                    <div className="liveChat_msg__MIVDo">
                                        Welcome to the{" "}
                                        {process.env.REACT_APP_PROJECT_NAME ||
                                            "Faceit"}{" "}
                                        support team. My name is{" "}
                                        {user?.support_consultant_name}.
                                        Describe your problems - in as much
                                        detail as possible and our support will
                                        help you in the nearest time!
                                    </div>
                                    <div className="liveChat_time__CrV86">
                                        {user?.support_consultant_name} ·{" "}
                                        {formatSupportDate(
                                            room?.created_at || Date.now()
                                        )}
                                        .
                                    </div>
                                </div>
                            </div>

                            {messages &&
                                messages.length > 0 &&
                                messages.map((message: any) => {
                                    const contentType = message.content_type;
                                    const content =
                                        message.content || message.message;

                                    return (
                                        <div
                                            className={`liveChat_message__NWRRw${
                                                message.author_type ===
                                                "customer"
                                                    ? " liveChat_isMe__Pby0z"
                                                    : ""
                                            }`}
                                        >
                                            {message.author_type !==
                                                "customer" && (
                                                <img
                                                    src={SupportImage}
                                                    className="liveChat_avatar__QU_yO liveChat_def__4MOxM"
                                                />
                                            )}

                                            <div className="liveChat_messageOver__DpX_v">
                                                <div
                                                    className="liveChat_msg__MIVDo"
                                                    style={{
                                                        width:
                                                            contentType ===
                                                            "image"
                                                                ? "100%"
                                                                : "auto",
                                                    }}
                                                >
                                                    {contentType === "text" ? (
                                                        <span>{content}</span>
                                                    ) : (
                                                        <img
                                                            style={{
                                                                width: "100%",
                                                            }}
                                                            src={content}
                                                            alt="Uploaded"
                                                            onClick={() =>
                                                                setCurrentImage(
                                                                    content
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </div>

                                                {user?.support_consultant_name && (
                                                    <div className="liveChat_time__CrV86">
                                                        {
                                                            user.support_consultant_name
                                                        }{" "}
                                                        ·{" "}
                                                        {formatSupportDate(
                                                            message.created_at
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>

                        <div className="liveChat_bottom___dKA0">
                            <div className="liveChat_overInput__sYxkX ">
                                <input
                                    className="liveChat_input__SpC1t"
                                    placeholder="Message..."
                                    value={currentText}
                                    onChange={(e: any) =>
                                        setCurrentText(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        e.key === "Enter" && sendMessage();
                                    }}
                                    maxLength={250}
                                />

                                <button
                                    onClick={() => fileInputRef.current.click()}
                                    className={`less${file ? " active" : ""}`}
                                >
                                    <svg
                                        width={16}
                                        height={14}
                                        viewBox="0 0 16 14"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.63908 0.290372C7.825 0.476321 7.92945 0.728506 7.92945 0.991459C7.92945 1.25441 7.825 1.5066 7.63908 1.69255L2.80262 6.529C2.27111 7.06964 1.97473 7.79838 1.97801 8.55653C1.9813 9.31467 2.28398 10.0408 2.82015 10.5768C3.35633 11.1129 4.08256 11.4153 4.84071 11.4184C5.59885 11.4215 6.32751 11.1249 6.868 10.5932L13.7797 3.68032C13.9517 3.49547 14.0453 3.25111 14.0408 2.99865C14.0364 2.74618 13.9341 2.5053 13.7557 2.32668C13.5772 2.14806 13.3364 2.04562 13.0839 2.04092C12.8315 2.03622 12.587 2.12962 12.402 2.30148L5.53931 9.16303C5.35249 9.34526 5.10135 9.44649 4.84038 9.44476C4.5794 9.44302 4.32963 9.33847 4.14524 9.15378C3.96086 8.96908 3.85672 8.71914 3.85542 8.45816C3.85412 8.19718 3.95577 7.94621 4.13831 7.75969L10.9987 0.90047C11.5576 0.366763 12.3033 0.0729786 13.076 0.0819596C13.8488 0.0909407 14.5874 0.401974 15.1338 0.948527C15.6802 1.49508 15.991 2.23375 15.9998 3.00654C16.0086 3.77932 15.7146 4.52486 15.1807 5.08366L8.25851 12.0059C7.34462 12.9021 6.11393 13.4014 4.83396 13.3952C3.55398 13.389 2.32817 12.8778 1.42301 11.9728C0.517836 11.0678 0.00646897 9.84209 6.09543e-05 8.56211C-0.00634707 7.28213 0.492722 6.05136 1.38878 5.13732L1.39578 5.13149L6.23573 0.290372C6.42168 0.104449 6.67387 0 6.93682 0C7.19977 0 7.45313 0.104449 7.63908 0.290372Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </button>

                                <button
                                    className="less"
                                    disabled={
                                        (!file && !currentText.trim()) ||
                                        !wsConnected
                                    }
                                    onClick={sendMessage}
                                >
                                    <svg
                                        width={16}
                                        height={18}
                                        viewBox="0 0 16 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M7 17C7 17.5523 7.44772 18 8 18C8.55228 18 9 17.5523 9 17H7ZM8.70711 0.292893C8.31658 -0.0976311 7.68342 -0.0976311 7.29289 0.292893L0.928932 6.65685C0.538408 7.04738 0.538408 7.68054 0.928932 8.07107C1.31946 8.46159 1.95262 8.46159 2.34315 8.07107L8 2.41421L13.6569 8.07107C14.0474 8.46159 14.6805 8.46159 15.0711 8.07107C15.4616 7.68054 15.4616 7.04738 15.0711 6.65685L8.70711 0.292893ZM9 17V1H7V17H9Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="liveChat_main__06_VH">
                <div className="liveChat_tab__UU4cn ">Live support</div>
                <div
                    className="liveChat_circle__S8bvP"
                    onClick={() => setChatOpened(!chatOpened)}
                >
                    <svg
                        className="liveChat_imgIn___oMkJ liveChat_active__PJwGv"
                        width={40}
                        height={40}
                        viewBox="0 0 40 40"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ color: "#000" }}
                    >
                        <path
                            d="M25.4158 6.67216H14.5842C8.3852 6.67216 3.336 11.7214 3.336 17.9203V32.0848C3.336 32.768 3.90256 33.3346 4.58584 33.3346H25.4158C31.6148 33.3346 36.664 28.2854 36.664 22.0864V17.9204C36.664 11.7214 31.6148 6.67216 25.4158 6.67216ZM28.7153 20.4033C28.6819 20.5366 28.6486 20.6699 28.582 20.8033C28.532 20.9198 28.4653 21.0365 28.3986 21.1531C28.3189 21.266 28.2297 21.372 28.132 21.4698C28.0487 21.5698 27.932 21.653 27.8154 21.7364C27.7154 21.803 27.5822 21.8697 27.4654 21.9197C27.3322 21.9697 27.1989 22.0197 27.0655 22.0363C26.9348 22.0693 26.8005 22.0861 26.6656 22.0863C26.1157 22.0863 25.5824 21.853 25.1992 21.4698C25.1015 21.372 25.0123 21.266 24.9326 21.1531C24.8659 21.0365 24.7992 20.9198 24.7492 20.8032C24.6826 20.6699 24.6492 20.5366 24.6159 20.4032C24.5993 20.2699 24.5826 20.1366 24.5826 20.0034C24.5826 19.4534 24.7992 18.9202 25.1992 18.5202C25.2825 18.4369 25.3992 18.337 25.5158 18.2702C25.6158 18.187 25.7491 18.1203 25.8658 18.0702C25.999 18.0203 26.1323 17.987 26.2657 17.9537C26.9322 17.8203 27.6654 18.037 28.1321 18.5202C28.532 18.9202 28.7486 19.4534 28.7486 20.0034C28.7486 20.1366 28.7319 20.2699 28.7153 20.4033ZM22.083 20.0033C22.083 20.5533 21.8664 21.0865 21.4664 21.4698C21.3831 21.5698 21.2664 21.653 21.1498 21.7364C21.0498 21.803 20.9166 21.8697 20.7998 21.9197C20.6666 21.9697 20.5333 22.0197 20.3999 22.0363C20.2692 22.0693 20.1349 22.0861 20 22.0863C19.8667 22.0863 19.7334 22.0697 19.6 22.0363C19.4667 22.0197 19.3334 21.9697 19.2002 21.9197C19.0834 21.8697 18.9502 21.803 18.8502 21.7364C18.7335 21.653 18.6169 21.5698 18.5336 21.4698C18.1336 21.0866 17.917 20.5533 17.917 20.0034C17.917 19.4534 18.1336 18.9202 18.5336 18.5202C18.6169 18.4369 18.7336 18.337 18.8502 18.2702C18.9502 18.187 19.0834 18.137 19.2002 18.0702C19.3334 18.0203 19.4667 17.987 19.6001 17.9537C19.8644 17.9037 20.1357 17.9037 20.3999 17.9537C20.5333 17.987 20.6666 18.0203 20.7999 18.0703C20.9166 18.137 21.0498 18.187 21.1498 18.2703C21.2665 18.337 21.3831 18.4369 21.4664 18.5202C21.8664 18.9202 22.083 19.4534 22.083 20.0033ZM11.868 18.5369C11.9513 18.4369 12.068 18.3369 12.1846 18.2702C12.2846 18.187 12.4178 18.137 12.5346 18.0702C12.6678 18.0203 12.8011 17.987 12.9345 17.9537C13.1988 17.9037 13.4701 17.9037 13.7343 17.9537C13.8677 17.987 14.001 18.0203 14.1343 18.0703C14.2509 18.137 14.3842 18.187 14.4842 18.2703C14.6009 18.337 14.7175 18.4369 14.8008 18.5369C15.2008 18.9202 15.4174 19.4534 15.4174 20.0033C15.4174 20.5366 15.2008 21.0865 14.8008 21.4698C14.7175 21.5698 14.6008 21.653 14.4842 21.7364C14.3842 21.803 14.2509 21.8697 14.1342 21.9197C14.001 21.9697 13.8677 22.0197 13.7343 22.0363C13.6036 22.0693 13.4693 22.0861 13.3344 22.0863C13.2011 22.0863 13.0678 22.0697 12.9344 22.0363C12.8011 22.0197 12.6678 21.9697 12.5346 21.9197C12.4178 21.8697 12.2846 21.803 12.1846 21.7364C12.0679 21.653 11.9513 21.5698 11.868 21.4698C11.468 21.0866 11.2514 20.5366 11.2514 20.0034C11.2514 19.4534 11.468 18.9201 11.868 18.5369Z"
                            fill="currentColor"
                        />
                    </svg>
                    <div
                        className={`liveChat_unread__6H_9b${
                            unreadMessages > 0 ? " liveChat_active__PJwGv" : ""
                        }`}
                    >
                        {unreadMessages}
                    </div>
                </div>
            </div>

            {currentImage && (
                <div
                    className="detailed-image-wrapper"
                    onClick={() => setCurrentImage("")}
                >
                    <div className="detailed-image-wrapper-cross">
                        {" "}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            xmlnsXlink="http://www.w3.org/1999/xlink"
                            fill="currentColor"
                            version="1.1"
                            viewBox="0 0 94.926 94.926"
                            xmlSpace="preserve"
                        >
                            <path d="M55.931,47.463L94.306,9.09c0.826-0.827,0.826-2.167,0-2.994L88.833,0.62C88.436,0.224,87.896,0,87.335,0   c-0.562,0-1.101,0.224-1.498,0.62L47.463,38.994L9.089,0.62c-0.795-0.795-2.202-0.794-2.995,0L0.622,6.096   c-0.827,0.827-0.827,2.167,0,2.994l38.374,38.373L0.622,85.836c-0.827,0.827-0.827,2.167,0,2.994l5.473,5.476   c0.397,0.396,0.936,0.62,1.498,0.62s1.1-0.224,1.497-0.62l38.374-38.374l38.374,38.374c0.397,0.396,0.937,0.62,1.498,0.62   s1.101-0.224,1.498-0.62l5.473-5.476c0.826-0.827,0.826-2.167,0-2.994L55.931,47.463z" />
                        </svg>
                    </div>
                    <img
                        src={currentImage}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    ) : null;
};

export default Support;
